import {
  NO_RACE_LABEL,
  distinctSorted,
  findChipMatches,
  formatDateIso,
  normalizeChipCode,
  pickForEnter,
  type AlreadyReturnedDto,
  type ChipDto,
  type EventStateDto,
  type ImportResultDto,
  type ReturnAction,
  type ServerMessage,
} from '@pietrochip/shared';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as api from '../api/endpoints';
import { ApiError, describeError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { ChipList } from '../components/ChipList';
import { ImportWizard } from '../components/ImportWizard';
import { RemovedChips } from '../components/RemovedChips';
import { SearchResults } from '../components/SearchResults';
import { useToasts } from '../components/Toasts';
import { EmptyState, Modal, Notice, ProgressBar, Stat } from '../components/ui';
import { APP_TIME_ZONE, eventDate, percent, personName, time } from '../lib/format';
import { fallbackExportName, saveBlob } from '../lib/download';
import { useAudioSettings } from '../lib/audioSettings';
import { playCue, unlockAudio } from '../lib/sounds';
import { useFallbackPoll } from '../realtime/useFallbackPoll';
import { useReconnectingSocket } from '../realtime/useReconnectingSocket';
import {
  EMPTY_EVENT_STATE,
  eventReducer,
  selectChips,
  selectRaces,
  selectRecentlyReturned,
  selectRemovedChips,
  selectTotals,
} from '../state/eventState';

/**
 * La schermata di lavoro: è qui che si passano le ore.
 *
 * Tre cose, in ordine di importanza per chi sta in piedi davanti a un banco:
 * un campo di ricerca che risponde a ogni tasto, l'avanzamento della giornata
 * sempre sott'occhio, e la possibilità di tornare indietro su una restituzione
 * sbagliata senza cercare nulla.
 *
 * Il server resta l'unica fonte della verità: questa schermata ne tiene una
 * copia di lavoro, la aggiorna a ogni messaggio del socket, e quando scrive
 * aspetta la conferma. L'unica anticipazione è il pulsante premuto, e si
 * disfa da sola se il server risponde di no.
 */

const STATUS_FILTERS = {
  all: 'Tutti',
  missing: 'Solo mancanti',
  returned: 'Solo restituiti',
} as const;

type StatusFilter = keyof typeof STATUS_FILTERS;

/**
 * Come si legge il registro.
 *
 * Il verbo è al passato e regge il codice che segue («Rimosso VF45803»), quindi
 * ogni voce finisce con uno spazio. `remove` e `restore` compaiono qui perché
 * togliere un chip dall'elenco è un'operazione come le altre: chi rilegge la
 * giornata deve poter vedere anche quella, non solo i rientri.
 */
const LOG_LABELS: Record<ReturnAction, string> = {
  return: 'Restituito ',
  unreturn: 'Annullato ',
  remove: 'Rimosso ',
  restore: 'Ripristinato ',
};

/**
 * L'esito di un'importazione, detto per intero.
 *
 * Un import su un evento che ha già la sua lista fa tre cose diverse — aggiunge,
 * aggiorna, lascia stare — e il vecchio messaggio ne raccontava una sola: chi
 * aveva scelto «aggiorna» su un conflitto non trovava conferma da nessuna parte.
 * Le voci a zero si tacciono, e se sono tutte a zero l'importazione non ha
 * toccato niente e va detto.
 */
function describeImport(result: ImportResultDto): string {
  const parts = [
    result.imported > 0 ? `${result.imported} aggiunti` : null,
    result.updated > 0 ? `${result.updated} aggiornati` : null,
    result.skipped > 0 ? `${result.skipped} lasciati com’erano` : null,
  ].filter((part): part is string => part !== null);

  if (parts.length === 0) return 'Nessun chip importato: l’elenco era già completo.';
  return `Importazione: ${parts.join(', ')}.`;
}

/** Sotto questa lunghezza un codice non è credibile: si evita di creare chip per sbaglio. */
const MIN_ADDABLE_CODE_LENGTH = 3;

export function EventPage() {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { push } = useToasts();

  const [state, dispatch] = useReducer(eventReducer, EMPTY_EVENT_STATE);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [raceFilter, setRaceFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');

  const [busyChipId, setBusyChipId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  /** Archiviazione/ripristino in corso: disabilita il pulsante e basta. */
  const [archiving, setArchiving] = useState(false);
  /** Il chip in attesa di conferma prima della rimozione. */
  const [confirmingRemoval, setConfirmingRemoval] = useState<ChipDto | null>(null);
  const [removedLoading, setRemovedLoading] = useState(false);
  const [removedLoaded, setRemovedLoaded] = useState(false);

  const audio = useAudioSettings();
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Il segnale acustico di un esito.
   *
   * Si suona **solo** nei rami in cui l'operatore ha appena agito, mai dentro
   * `onMessage`: i messaggi del socket arrivano anche per le azioni degli altri
   * banchi, e un beep lì suonerebbe per tutti a ogni transponder mosso in
   * palazzetto.
   */
  const cue = useCallback(
    (outcome: 'ok' | 'already_returned' | 'unknown_code') => playCue(outcome, audio),
    [audio],
  );

  /**
   * Sblocca l'audio al primo gesto utile.
   *
   * I browser tengono muto un `AudioContext` creato senza un gesto dell'utente.
   * Il primo suono vero può arrivare dopo un `await` — quindi fuori dal gesto
   * che l'ha causato — e a quel punto sarebbe troppo tardi. Qui si sblocca
   * appena si tocca o si preme un tasto, che al banco succede subito.
   */
  useEffect(() => {
    const unlock = () => unlockAudio();
    document.addEventListener('pointerdown', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // ── Stato dal server ───────────────────────────────────────────────────────

  const onMessage = useCallback(
    (raw: unknown) => {
      const message = raw as ServerMessage | { type: 'pong' };

      switch (message.type) {
        case 'hello':
          // Arriva all'apertura e a **ogni riconnessione**: è la rilettura
          // completa dello stato dopo un buco di rete, senza bisogno di altro.
          dispatch({ type: 'server:state', state: message.state, operators: message.operators });
          setLoadError(null);
          break;

        case 'chip:returned':
        case 'chip:unreturned':
          dispatch({ type: 'server:chip', chip: message.chip, log: message.log });
          break;

        case 'chip:created':
          dispatch({ type: 'server:chip', chip: message.chip });
          break;

        case 'chips:imported':
          dispatch({ type: 'server:chips', chips: message.chips });
          break;

        case 'chips:updated':
          dispatch({ type: 'server:chips', chips: message.chips });
          break;

        case 'chip:removed':
          dispatch({ type: 'server:chip-removed', chip: message.chip, log: message.log });
          break;

        case 'chip:restored':
          dispatch({ type: 'server:chip-restored', chip: message.chip, log: message.log });
          break;

        case 'event:updated':
          dispatch({ type: 'server:event', event: message.event });
          break;

        case 'presence':
          dispatch({ type: 'server:presence', operators: message.operators });
          break;

        case 'event:deleted':
          push({ tone: 'danger', message: 'Questo evento è stato eliminato da un amministratore.' });
          navigate('/', { replace: true });
          break;

        default:
          break;
      }
    },
    [navigate, push],
  );

  const { status: socketStatus, generation: socketGeneration } = useReconnectingSocket(
    eventId ? `/api/events/${eventId}/stream` : null,
    onMessage,
  );

  // Lo stato serve dentro la richiamata del ripiego, che è nata in un render
  // precedente: attraverso una ref si legge sempre quello corrente.
  const stateRef = useRef(state);
  stateRef.current = state;
  const socketStatusRef = useRef(socketStatus);
  socketStatusRef.current = socketStatus;

  /**
   * Ricaricamento di riserva.
   *
   * Il socket manda lo stato completo da solo, ma se un proxy di rete non fa
   * passare l'aggiornamento a WebSocket — o lo blocca senza chiuderlo — la
   * schermata resterebbe ferma su quello che aveva. Questa è la rete di
   * sicurezza, e gira **solo** quando il socket non è aperto.
   *
   * È in due tempi perché lo stato intero di un evento da migliaia di chip pesa
   * qualche megabyte: prima si chiede l'impronta, e lo stato si scarica solo se
   * è cambiata. Alla prima lettura non c'è niente da confrontare, quindi si va
   * diretti allo stato — è quello che serve per dipingere la pagina.
   */
  const lastVersionRef = useRef<string | null>(null);

  const poll = useCallback(async () => {
    if (!eventId) return;

    const revisionAtStart = stateRef.current.revision;
    const statusAtStart = socketStatusRef.current;

    try {
      let version: string | null = null;

      if (stateRef.current.loaded) {
        const current = await api.getEventVersion(eventId);
        if (current.version === lastVersionRef.current) return;
        version = current.version;
      }

      const fresh: EventStateDto = await api.getEventState(eventId);

      // Fotografia nata prima di qualcosa che sappiamo già — una scrittura
      // nostra, un messaggio del socket, il `hello` di una connessione appena
      // aperta: applicarla riporterebbe indietro l'interfaccia sotto le dita
      // di chi la sta usando.
      if (
        stateRef.current.revision !== revisionAtStart ||
        socketStatusRef.current !== statusAtStart
      ) {
        return;
      }

      dispatch({ type: 'refresh:state', state: fresh });
      setLoadError(null);

      // L'impronta si registra solo dopo aver applicato: se l'aggiornamento
      // fosse stato scartato, il prossimo giro deve rileggere lo stato intero,
      // non saltarlo.
      lastVersionRef.current = version ?? (await api.getEventVersion(eventId)).version;
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setLoadError('Questo evento non esiste più.');
      }
      // Un giro perso non è un guasto da mostrare: i dati che ci sono restano
      // validi finché il server non risponde di nuovo.
    }
  }, [eventId]);

  useFallbackPoll({ active: eventId !== '', status: socketStatus, poll });

  // Stato azzerato quando si cambia evento: i chip di ieri non devono comparire
  // per un istante in cima a quelli di oggi.
  useEffect(() => {
    // L'impronta è legata all'evento: quella di ieri non deve far saltare la
    // prima lettura di oggi.
    lastVersionRef.current = null;
    dispatch({ type: 'reset' });
    setQuery('');
    setStatusFilter('all');
    setRaceFilter('');
    setTeamFilter('');
    setLoadError(null);
    // L'archivio è dell'evento: quello di prima non vale più, e la prossima
    // apertura della sezione deve rileggerlo.
    setRemovedLoaded(false);
    setConfirmingRemoval(null);
  }, [eventId]);

  /** `/` riporta al campo di ricerca da qualsiasi punto della pagina. */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }

      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // ── Dati derivati ──────────────────────────────────────────────────────────

  const chips = useMemo(() => selectChips(state), [state]);
  const totals = useMemo(() => selectTotals(state), [state]);
  const races = useMemo(() => selectRaces(state), [state]);
  const recentlyReturned = useMemo(() => selectRecentlyReturned(state, 6), [state]);
  const removedChips = useMemo(() => selectRemovedChips(state), [state]);
  const teams = useMemo(() => distinctSorted(chips.map((chip) => chip.team)), [chips]);
  const raceOptions = useMemo(
    () => distinctSorted(chips.map((chip) => chip.race), { fallbackLabel: NO_RACE_LABEL }),
    [chips],
  );

  const matches = useMemo(() => findChipMatches(chips, query, 40), [chips, query]);

  const normalizedQuery = normalizeChipCode(query);
  const canAdd = matches.length === 0 && normalizedQuery.length >= MIN_ADDABLE_CODE_LENGTH;

  const visibleChips = useMemo(
    () =>
      chips.filter((chip) => {
        if (statusFilter === 'missing' && chip.returnedAt !== null) return false;
        if (statusFilter === 'returned' && chip.returnedAt === null) return false;

        if (raceFilter !== '') {
          const chipRace = chip.race?.trim() ?? '';
          if (raceFilter === NO_RACE_LABEL) {
            if (chipRace !== '') return false;
          } else if (chipRace !== raceFilter) {
            return false;
          }
        }

        if (teamFilter !== '' && (chip.team?.trim() ?? '') !== teamFilter) return false;
        return true;
      }),
    [chips, statusFilter, raceFilter, teamFilter],
  );

  const filtersActive = statusFilter !== 'all' || raceFilter !== '' || teamFilter !== '';

  // ── Azioni ─────────────────────────────────────────────────────────────────

  /** Svuota la barra e ridà il fuoco: pronta per il prossimo transponder. */
  const resetSearch = useCallback(() => {
    setQuery('');
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const markReturned = useCallback(
    async (chip: ChipDto) => {
      if (chip.returnedAt !== null) {
        // Il suono dice quello che c'è da sapere prima ancora che l'occhio
        // arrivi allo schermo, e il campo si svuota come nell'altro ramo: senza
        // questo, il codice resterebbe scritto e la prossima scansione si
        // sommerebbe a quella.
        cue('already_returned');
        push({
          tone: 'missing',
          message: `Il chip ${chip.code} risulta già restituito alle ${time(chip.returnedAt)}.`,
        });
        resetSearch();
        return;
      }

      dispatch({
        type: 'optimistic:return',
        chip,
        user: { id: user?.id ?? '', name: user?.name ?? '' },
        at: new Date().toISOString(),
      });

      try {
        const { chip: updated, log } = await api.returnChip(eventId, chip.id);
        dispatch({ type: 'server:chip', chip: updated, log });
        cue('ok');

        push({
          tone: 'returned',
          message: (
            <>
              <span className="toast__code mono">{updated.code}</span>
              {` restituito${updated.lastName ? ` — ${personName(updated.firstName, updated.lastName)}` : ''}`}
            </>
          ),
          action: { label: 'Annulla', onClick: () => void unreturnChip(updated) },
        });

        resetSearch();
      } catch (cause) {
        if (cause instanceof ApiError && cause.code === 'already_returned') {
          // L'esito normale di due banchi che chiudono lo stesso chip quasi
          // insieme: il primo ha vinto, e il secondo lo vede scritto. Non è un
          // errore da correggere, è un'informazione.
          const body = cause.body as unknown as AlreadyReturnedDto;
          dispatch({ type: 'server:chip', chip: body.chip });
          cue('already_returned');
          push({ tone: 'missing', message: cause.message });
          resetSearch();
          return;
        }

        // Il server non ha accettato: si torna indietro e lo si dice.
        dispatch({ type: 'optimistic:rollback', chipId: chip.id });
        push({ tone: 'danger', message: describeError(cause) });
      }
    },
    // `unreturnChip` non è fra le dipendenze di proposito: viene chiamata solo
    // dentro l'azione dell'avviso, quando il componente ha già finito di
    // ridisegnarsi, e tenerla fuori evita che i due callback si richiamino a
    // vicenda a ogni render.
    [cue, eventId, push, resetSearch, user?.id, user?.name],
  );

  const unreturnChip = useCallback(
    async (chip: ChipDto) => {
      if (busyChipId !== null) return;
      setBusyChipId(chip.id);

      try {
        const { chip: updated, log } = await api.unreturnChip(eventId, chip.id);
        dispatch({ type: 'server:chip', chip: updated, log: log ?? undefined });
        push({ tone: 'neutral', message: `Restituzione di ${updated.code} annullata.` });
      } catch (cause) {
        push({ tone: 'danger', message: describeError(cause) });
      } finally {
        setBusyChipId(null);
      }
    },
    [busyChipId, eventId, push],
  );

  /**
   * Un codice che non è nell'elenco.
   *
   * Due esiti diversi a seconda di cosa ha in mano l'operatore: chi ha appena
   * ritirato il transponder lo chiude subito (`returnNow`), chi lo sta
   * registrando in anticipo lo aggiunge e basta. In entrambi i casi
   * `addedManually` segnala che quel chip non veniva dal file.
   */
  const addChip = useCallback(
    async (code: string, returnNow: boolean) => {
      setBusyChipId(code);

      try {
        const { chip, log } = await api.createChip(eventId, { code, returnNow });
        dispatch({ type: 'server:chip', chip, log: log ?? undefined });
        cue('ok');

        push({
          tone: returnNow ? 'returned' : 'neutral',
          message: (
            <>
              <span className="toast__code mono">{chip.code}</span>
              {returnNow ? ' aggiunto e segnato restituito.' : ' aggiunto all’elenco.'}
            </>
          ),
          action: returnNow
            ? { label: 'Annulla', onClick: () => void unreturnChip(chip) }
            : undefined,
        });

        resetSearch();
      } catch (cause) {
        if (cause instanceof ApiError && cause.code === 'already_exists') {
          // Un altro banco l'ha aggiunto nel frattempo: non è un errore, è la
          // risposta alla domanda «l'avevo già registrato io?».
          const existing = (cause.body as { chip?: ChipDto }).chip;
          if (existing) {
            dispatch({ type: 'server:chip', chip: existing });
            cue('already_returned');
            push({ tone: 'missing', message: 'Questo codice è appena comparso nell’evento.' });
            resetSearch();
          }
        } else {
          push({ tone: 'danger', message: describeError(cause) });
        }
      } finally {
        setBusyChipId(null);
      }
    },
    [cue, eventId, push, resetSearch, unreturnChip],
  );

  const addAndReturn = useCallback(
    (code: string) => addChip(code, true),
    [addChip],
  );

  const addOnly = useCallback((code: string) => addChip(code, false), [addChip]);

  const performRemove = useCallback(
    async (chip: ChipDto) => {
      if (busyChipId !== null) return;
      setBusyChipId(chip.id);
      setConfirmingRemoval(null);

      try {
        const { chip: removed, log } = await api.removeChip(eventId, chip.id);
        dispatch({ type: 'server:chip-removed', chip: removed, log });

        push({
          tone: 'missing',
          message: (
            <>
              <span className="toast__code mono">{removed.code}</span> rimosso dall’elenco.
            </>
          ),
          action: { label: 'Annulla', onClick: () => void performRestore(removed) },
        });
      } catch (cause) {
        if (cause instanceof ApiError && cause.code === 'already_removed') {
          // Un altro banco l'ha già archiviato: lo stato desiderato è quello.
          const body = (cause.body as { chip?: ChipDto }).chip;
          if (body) dispatch({ type: 'server:chip-removed', chip: body });
          push({ tone: 'missing', message: cause.message });
        } else {
          push({ tone: 'danger', message: describeError(cause) });
        }
      } finally {
        setBusyChipId(null);
      }
    },
    [busyChipId, eventId, push],
  );

  const performRestore = useCallback(
    async (chip: ChipDto) => {
      if (busyChipId !== null) return;
      setBusyChipId(chip.id);

      try {
        const { chip: restored, log } = await api.restoreChip(eventId, chip.id);
        dispatch({ type: 'server:chip-restored', chip: restored, log: log ?? undefined });
        push({ tone: 'neutral', message: `${restored.code} ripristinato nell’elenco.` });
      } catch (cause) {
        if (cause instanceof ApiError && cause.code === 'code_conflict') {
          // Il codice è stato reimportato mentre era archiviato: il chip attivo
          // non si tocca, e quello archiviato resta dov'è.
          push({ tone: 'danger', message: cause.message });
        } else {
          push({ tone: 'danger', message: describeError(cause) });
        }
      } finally {
        setBusyChipId(null);
      }
    },
    [busyChipId, eventId, push],
  );

  /**
   * Rimuove un chip: prima la conferma, e solo se serve.
   *
   * La conferma si chiede **solo** quando il chip risulta già restituito. È il
   * caso in cui l'operatore sta per togliere dall'elenco un transponder che
   * qualcuno ha già riconsegnato — e se l'ha fatto per sbaglio se ne accorge
   * tardi. Su un chip mai rientrato la rimozione è l'azione con cui si corregge
   * un codice sbagliato, e farla passare da un modale la renderebbe una
   * seccatura per l'uso più frequente.
   */
  const askRemove = useCallback(
    (chip: ChipDto) => {
      if (chip.returnedAt !== null) {
        setConfirmingRemoval(chip);
        return;
      }
      void performRemove(chip);
    },
    [performRemove],
  );

  /**
   * Carica l'elenco dei rimossi la prima volta che serve.
   *
   * Non fa parte dello stato iniziale: la pagina non deve scaricare i chip
   * archiviati a ogni apertura, perché chi ha il transponder in mano non li sta
   * cercando. Da qui in poi li tengono aggiornati i messaggi del socket.
   *
   * `force` serve alla riapertura del socket: lì la copia incrementale può essere
   * incompleta, e va riletta anche se era già stata caricata.
   */
  const loadRemoved = useCallback(
    async (force = false) => {
      if (!eventId) return;
      if (removedLoading) return;
      if (!force && removedLoaded) return;

      setRemovedLoading(true);

      try {
        const { chips: removed } = await api.fetchRemovedChips(eventId);
        dispatch({ type: 'removed:state', chips: removed });
        setRemovedLoaded(true);
      } catch (cause) {
        push({ tone: 'danger', message: describeError(cause) });
      } finally {
        setRemovedLoading(false);
      }
    },
    [eventId, push, removedLoaded, removedLoading],
  );

  /**
   * L'archivio si rilegge a ogni riapertura del socket.
   *
   * È l'unica parte dello stato che `hello` non rimanda: i chip attivi arrivano
   * tutti, i rimossi no, perché il server non li manda a chi non li ha chiesti.
   * Dopo un buco di rete la copia tenuta qui può quindi essere incompleta — e la
   * si rilegge **solo** se qualcuno aveva aperto la sezione, così chi non l'ha
   * mai guardata non paga niente. La funzione si legge da una ref perché
   * l'effetto deve dipendere dalla sola riapertura: seguirne l'identità
   * significherebbe rilanciarlo a ogni cambio di `removedLoading`.
   */
  const loadRemovedRef = useRef(loadRemoved);
  loadRemovedRef.current = loadRemoved;
  const removedLoadedRef = useRef(removedLoaded);
  removedLoadedRef.current = removedLoaded;

  useEffect(() => {
    if (socketGeneration === 0 || !removedLoadedRef.current) return;
    void loadRemovedRef.current(true);
  }, [socketGeneration]);

  /**
   * Invio nel campo di ricerca: chiude il chip solo se non c'è ambiguità.
   *
   * Con un codice che non esiste **non si aggiunge più niente**: si suona
   * l'avviso e si lascia il pannello con le scelte. Al banco il lettore barcode
   * preme Invio da solo, e un codice letto male finiva nell'elenco senza che
   * nessuno l'avesse deciso — poi toglierlo costava una ricerca e una conferma.
   */
  function onSearchKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const target = pickForEnter(matches);
    if (target) {
      void markReturned(target);
      return;
    }

    if (canAdd) {
      cue('unknown_code');
      // Il testo si riseleziona: se il codice è stato letto male, la correzione
      // è digitare sopra, senza cancellare a mano.
      inputRef.current?.select();
    }
  }

  /**
   * Archivia l'evento o lo riporta attivo.
   *
   * Il server risponde con l'evento aggiornato e lo propaga anche a questo
   * socket: lo stato locale si aggiorna per due strade, la stessa cosa due
   * volte, e va bene così — il banner e il pulsante si allineano subito senza
   * aspettare il giro di rete.
   */
  async function toggleArchive(archivedNow: boolean) {
    if (archiving || !state.event) return;
    setArchiving(true);

    try {
      const { event: updated } = archivedNow
        ? await api.archiveEvent(eventId)
        : await api.unarchiveEvent(eventId);

      dispatch({ type: 'server:event', event: updated });
      push({
        tone: 'neutral',
        message: archivedNow
          ? 'Evento archiviato: non compare più nella home.'
          : 'Evento riportato negli eventi attivi.',
      });
    } catch (cause) {
      push({ tone: 'danger', message: describeError(cause) });
    } finally {
      setArchiving(false);
    }
  }

  async function runExport() {
    if (exporting || !state.event) return;
    setExporting(true);

    try {
      const { blob, fileName } = await api.downloadExport(eventId);
      saveBlob(
        blob,
        fileName ?? fallbackExportName(state.event.name, formatDateIso(new Date(), APP_TIME_ZONE)),
      );
      push({ tone: 'neutral', message: 'Riepilogo scaricato.' });
    } catch (cause) {
      push({ tone: 'danger', message: describeError(cause) });
    } finally {
      setExporting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div className="stack">
        <Notice tone="danger">{loadError}</Notice>
        <div className="row">
          <button type="button" className="btn" onClick={() => navigate('/')}>
            Torna agli eventi
          </button>
        </div>
      </div>
    );
  }

  if (!state.loaded || !state.event) {
    return <p className="muted">Caricamento dell’evento…</p>;
  }

  const event = state.event;
  const isAdmin = user?.role === 'admin';

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">{event.name}</h1>
          <p className="page-head__meta">
            {event.date ? eventDate(event.date) : 'Senza data'}
            {' · '}
            {state.operators === 1
              ? '1 operatore collegato'
              : `${state.operators} operatori collegati`}
          </p>
        </div>

        <div className="page-head__actions">
          {/*
            Il registro non sta nel menù di `Shell`: quel menù non conosce
            l'evento, e «Squadre e registro» senza un evento non vuol dire niente.
            Si entra da qui, che è il posto in cui si sta già lavorando.
          */}
          <Link className="btn" to={`/eventi/${eventId}/operazioni`}>
            Squadre e registro
          </Link>
          <button type="button" className="btn" onClick={() => setImporting(true)}>
            Importa chip
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={exporting || totals.total === 0}
            onClick={() => void runExport()}
          >
            {exporting ? 'Preparazione…' : 'Esporta riepilogo'}
          </button>

          {/*
            Archiviare a fine giornata è l'ultimo gesto sull'evento. Sta qui e
            non solo sulla home perché è qui che si sa che l'evento è finito.
          */}
          {isAdmin &&
            (event.archivedAt !== null ? (
              <button
                type="button"
                className="btn"
                disabled={archiving}
                onClick={() => void toggleArchive(false)}
              >
                Ripristina
              </button>
            ) : (
              <button
                type="button"
                className="btn"
                disabled={archiving}
                onClick={() => void toggleArchive(true)}
              >
                Archivia
              </button>
            ))}
        </div>
      </div>

      {event.archivedAt !== null && (
        <div className="banner" role="status">
          <span>
            Questo evento è archiviato: non compare nella home, ma i suoi chip, il registro e il
            riepilogo restano intatti. {isAdmin ? 'Puoi ripristinarlo quando vuoi.' : ''}
          </span>
        </div>
      )}

      {socketStatus !== 'open' && (
        <div className="banner" role="status">
          <span
            className="offline__pulse"
            style={{ width: 14, height: 14, borderWidth: 2 }}
            aria-hidden="true"
          />
          <span>
            {socketStatus === 'connecting'
              ? 'Collegamento in corso…'
              : 'Riconnessione in corso: quello che vedi potrebbe non essere aggiornato.'}
          </span>
        </div>
      )}

      {totals.total === 0 ? (
        <div className="card">
          <EmptyState
            title="Nessun chip in questo evento"
            hint="Importa l’elenco dei transponder consegnati: da lì in poi si segnano le restituzioni."
          />
          <div style={{ padding: '0 18px 18px' }}>
            <button type="button" className="btn btn--primary btn--lg" onClick={() => setImporting(true)}>
              Importa i chip
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Banda riepilogativa: sempre in vista, si aggiorna da sola. */}
          <div className="card">
            <div className="card__body stack">
              <div className="stat-grid">
                <Stat label="Consegnati" value={totals.total} />
                <Stat label="Restituiti" value={totals.returned} tone="ok" />
                <Stat label="Mancanti" value={totals.missing} tone="warn" />
                <Stat label="Completamento" value={percent(totals.percent, 0)} />
              </div>
              <ProgressBar
                large
                fraction={totals.percent}
                label={`Restituiti ${totals.returned} su ${totals.total}`}
              />
            </div>
          </div>

          {/* Ricerca: il cuore della schermata. */}
          <div>
            <div className="searchbar">
              <input
                ref={inputRef}
                className="searchbar__input"
                type="search"
                value={query}
                onChange={(changeEvent) => setQuery(changeEvent.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Codice chip…"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
                enterKeyHint="done"
                aria-label="Cerca un chip per codice"
                aria-describedby="search-help"
                // Il campo è il punto di partenza: chi apre la schermata sta per
                // scansionare un codice, non per leggere.
                autoFocus
              />
              {query !== '' && (
                <button
                  type="button"
                  className="searchbar__clear"
                  onClick={resetSearch}
                  aria-label="Svuota la ricerca"
                >
                  ✕
                </button>
              )}
            </div>

            <p className="sr-only" id="search-help">
              Digita o scansiona il codice del transponder. Premi Invio per segnarlo restituito;
              premi la barra obliqua per tornare qui da qualsiasi punto della pagina.
            </p>

            {/*
              L'annuncio ai lettori di schermo è una riga sola, non la lista: far
              rileggere dieci risultati a ogni tasto premuto renderebbe la
              ricerca inutilizzabile con la voce.
            */}
            <p className="sr-only" aria-live="polite">
              {query === ''
                ? ''
                : matches.length === 0
                  ? canAdd
                    ? 'Nessun chip trovato. Puoi aggiungerlo e segnarlo restituito.'
                    : 'Nessun chip trovato.'
                  : matches.length === 1
                    ? 'Un chip trovato.'
                    : `${matches.length} chip trovati.`}
            </p>

            {query !== '' && (
              <SearchResults
                matches={matches}
                canAdd={canAdd}
                addCode={normalizedQuery}
                busyChipId={busyChipId}
                onReturn={(chip) => void markReturned(chip)}
                onUnreturn={(chip) => void unreturnChip(chip)}
                onAddAndReturn={(code) => void addAndReturn(code)}
                onAddOnly={(code) => void addOnly(code)}
                onCorrectCode={() => {
                  inputRef.current?.focus();
                  inputRef.current?.select();
                }}
                onRemove={askRemove}
              />
            )}
          </div>

          <div className="grid-2">
            <div className="stack" style={{ gap: 18 }}>
              {races.length > 1 && (
                <section className="stack">
                  <h2 className="card__title">Avanzamento per gara</h2>
                  <div className="races">
                    {races.map((race) => (
                      <button
                        key={race.race}
                        type="button"
                        className="race-tile"
                        aria-pressed={raceFilter === race.race}
                        onClick={() =>
                          setRaceFilter((current) => (current === race.race ? '' : race.race))
                        }
                      >
                        <span className="race-tile__name">{race.race}</span>
                        <span className="race-tile__counts">
                          {race.returned}/{race.total} · {percent(race.percent, 0)}
                        </span>
                        <ProgressBar
                          fraction={race.percent}
                          label={`Restituiti per ${race.race}`}
                        />
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section className="stack">
                <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <h2 className="card__title">Elenco chip</h2>
                  <span className="shell__spacer" />

                  <label className="sr-only" htmlFor="filter-status">
                    Filtra per stato
                  </label>
                  <select
                    id="filter-status"
                    className="select"
                    style={{ width: 'auto' }}
                    value={statusFilter}
                    onChange={(changeEvent) =>
                      setStatusFilter(changeEvent.target.value as StatusFilter)
                    }
                  >
                    {Object.entries(STATUS_FILTERS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>

                  {raceOptions.length > 1 && (
                    <>
                      <label className="sr-only" htmlFor="filter-race">
                        Filtra per gara
                      </label>
                      <select
                        id="filter-race"
                        className="select"
                        style={{ width: 'auto' }}
                        value={raceFilter}
                        onChange={(changeEvent) => setRaceFilter(changeEvent.target.value)}
                      >
                        <option value="">Tutte le gare</option>
                        {raceOptions.map((race) => (
                          <option key={race} value={race}>
                            {race}
                          </option>
                        ))}
                      </select>
                    </>
                  )}

                  {teams.length > 1 && (
                    <>
                      <label className="sr-only" htmlFor="filter-team">
                        Filtra per squadra
                      </label>
                      <select
                        id="filter-team"
                        className="select"
                        style={{ width: 'auto' }}
                        value={teamFilter}
                        onChange={(changeEvent) => setTeamFilter(changeEvent.target.value)}
                      >
                        <option value="">Tutte le squadre</option>
                        {teams.map((team) => (
                          <option key={team} value={team}>
                            {team}
                          </option>
                        ))}
                      </select>
                    </>
                  )}

                  {filtersActive && (
                    <button
                      type="button"
                      className="btn btn--sm btn--quiet"
                      onClick={() => {
                        setStatusFilter('all');
                        setRaceFilter('');
                        setTeamFilter('');
                      }}
                    >
                      Azzera filtri
                    </button>
                  )}
                </div>

                <ChipList
                  chips={visibleChips}
                  totalCount={chips.length}
                  busyChipId={busyChipId}
                  onReturn={(chip) => void markReturned(chip)}
                  onUnreturn={(chip) => void unreturnChip(chip)}
                  onRemove={askRemove}
                />
              </section>
            </div>

            <aside className="stack" style={{ gap: 18 }}>
              <section className="card">
                <div className="card__body stack">
                  <h2 className="card__title">Ultimi restituiti</h2>
                  {recentlyReturned.length === 0 ? (
                    <p className="muted" style={{ fontSize: '0.875rem' }}>
                      Ancora nessuna restituzione.
                    </p>
                  ) : (
                    <ul className="log-list">
                      {recentlyReturned.map((chip) => (
                        <li key={chip.id} className="log-item">
                          <span className="log-item__time">{time(chip.returnedAt)}</span>
                          <span className="log-item__text">
                            <span className="mono">{chip.code}</span>
                            {chip.lastName ? ` · ${personName(chip.firstName, chip.lastName)}` : ''}
                          </span>
                          {chip.returnedByName && (
                            <span className="chip-row__meta">{chip.returnedByName}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              {/*
                Il registro include anche le correzioni, non solo le
                restituzioni: è la traccia che serve a capire chi ha toccato
                cosa, e a ricostruire la giornata se qualcosa non torna.
              */}
              <section className="card">
                <div className="card__body stack">
                  <h2 className="card__title">Registro operazioni</h2>
                  {state.recentLogs.length === 0 ? (
                    <p className="muted" style={{ fontSize: '0.875rem' }}>
                      Nessuna operazione registrata.
                    </p>
                  ) : (
                    <ul className="log-list">
                      {state.recentLogs.map((log) => (
                        <li
                          key={log.id}
                          className={`log-item ${
                            log.action === 'unreturn' || log.action === 'remove'
                              ? 'log-item--unreturn'
                              : ''
                          }`}
                        >
                          <span className="log-item__time">{time(log.at)}</span>
                          <span className="log-item__text">
                            {LOG_LABELS[log.action] ?? 'Toccato '}
                            <span className="mono">{log.code}</span>
                          </span>
                          {log.userName && <span className="chip-row__meta">{log.userName}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </aside>
          </div>
        </>
      )}

      {/*
        La sezione dei rimossi sta **fuori** dal ramo dei totali: se si
        archiviano tutti i chip di un evento sbagliato, il totale scende a zero e
        la schermata mostrerebbe «Nessun chip in questo evento» — con la sezione
        nascosta dentro quel ramo non ci sarebbe più modo di ripristinarli.
      */}
      <RemovedChips
        chips={removedChips}
        loading={removedLoading}
        busyChipId={busyChipId}
        onOpen={() => void loadRemoved()}
        onRestore={(chip) => void performRestore(chip)}
      />

      {confirmingRemoval && (
        <Modal
          title="Rimuovere un chip già restituito?"
          onClose={() => setConfirmingRemoval(null)}
          footer={
            <>
              <span className="shell__spacer" />
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => setConfirmingRemoval(null)}
              >
                Annulla
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => void performRemove(confirmingRemoval)}
              >
                Rimuovi dall’elenco
              </button>
            </>
          }
        >
          <p>
            Il chip <span className="mono">{confirmingRemoval.code}</span>
            {confirmingRemoval.lastName
              ? ` di ${personName(confirmingRemoval.firstName, confirmingRemoval.lastName)}`
              : ''}{' '}
            risulta già restituito
            {confirmingRemoval.returnedAt ? ` alle ${time(confirmingRemoval.returnedAt)}` : ''}
            {confirmingRemoval.returnedByName ? ` da ${confirmingRemoval.returnedByName}` : ''}.
          </p>
          <p className="muted">
            Toglierlo dall’elenco lo farà sparire dai totali e dal riepilogo. La restituzione
            resta nel registro, e il chip si può ripristinare dalla sezione «Rimossi».
          </p>
        </Modal>
      )}

      {importing && (
        <ImportWizard
          eventId={eventId}
          onClose={() => setImporting(false)}
          onImported={(result) => {
            dispatch({ type: 'server:chips', chips: result.chips });
            // I chip aggiornati sono già nella mappa con lo stesso id, ma con i
            // dati vecchi: senza questo, l'anteprima mostrerebbe i nomi di prima
            // fino al prossimo `hello`.
            dispatch({ type: 'server:chips', chips: result.updatedChips });
            setImporting(false);
            push({ tone: 'neutral', message: describeImport(result) });
            window.requestAnimationFrame(() => inputRef.current?.focus());
          }}
        />
      )}
    </div>
  );
}
